/**
 * VTP Response Parser
 *
 * Parses the VTP API response and extracts all VTP-specific data including
 * vehicle details, prices, and dealer photos.
 */

import type { IVtpDetails, IVtpPhoto } from '../types';
import type { IVtpResponse, IVtpItem, IVtpItemValue } from './vtp.types';
import { VTP_ITEM_KEYS, VTP_PRICE_KEYS } from './vtp.types';

/**
 * Result of parsing a VTP response
 */
export interface IVtpParsedResult {
  vin: string;
  vtpDetails: IVtpDetails;
  vtpPhotos: IVtpPhoto[];
}

/**
 * Finds an item by key in the VTP items array
 */
const findItem = (items: IVtpItem[], key: string): IVtpItem | undefined => items.find((item) => item.key === key);

/**
 * Finds a price value by key in the prices item
 */
const findPriceValue = (pricesItem: IVtpItem | undefined, priceKey: string): IVtpItemValue | undefined =>
  pricesItem?.values?.find((v) => v.key === priceKey);

/**
 * Extracts dealer photos from the VTP response using functional array methods
 */
const extractDealerPhotos = (car: IVtpResponse['cars'][0]['car']): IVtpPhoto[] => {
  const dealerImagesGroup = car.images?.find((img) => img.key === 'dealerImages');

  return (
    dealerImagesGroup?.imageGroup?.images
      ?.filter((imageEntry) => imageEntry.image?.href)
      .map((imageEntry) => ({
        url: imageEntry.image.href,
        isRealPhoto: true, // All dealer images are real photos
        width: imageEntry.image.width,
        height: imageEntry.image.height,
      })) ?? []
  );
};

/**
 * Formats mileage with unit if available
 */
const formatMileage = (mileageItem: IVtpItem | undefined): string | undefined => {
  if (!mileageItem?.value) return undefined;
  return mileageItem.unit ? `${mileageItem.value} ${mileageItem.unit}` : mileageItem.value;
};

/**
 * Extracts VTP details from the items array using immutable object construction
 * Only extracts fields specified in AC2: price, firstRegistration, mileage, commissionNumber
 */
const extractVtpDetails = (items: IVtpItem[]): IVtpDetails => {
  const pricesItem = findItem(items, VTP_ITEM_KEYS.PRICES);
  const firstRegistration = findItem(items, VTP_ITEM_KEYS.INITIAL_REG)?.value;
  const mileage = formatMileage(findItem(items, VTP_ITEM_KEYS.MILEAGE));
  const commissionNumber = findItem(items, VTP_ITEM_KEYS.KNR)?.value;
  const price = findPriceValue(pricesItem, VTP_PRICE_KEYS.SALE)?.formattedValue;

  // Build object immutably with only defined properties
  return {
    ...(firstRegistration && { firstRegistration }),
    ...(mileage && { mileage }),
    ...(price && { price }),
    ...(commissionNumber && { commissionNumber }),
  };
};

/**
 * Parses the full VTP API response and extracts all relevant data
 *
 * @param response - The VTP API response
 * @returns Parsed VTP data including VIN, details, and photos
 * @throws Error if no cars found or VIN is missing
 */
export const parseVtpResponse = (response: IVtpResponse): IVtpParsedResult => {
  const [firstCar] = response.cars ?? [];

  if (!firstCar) {
    throw new Error('No cars found in VTP response');
  }

  const { car } = firstCar;
  const { items } = car;

  const vin = findItem(items, VTP_ITEM_KEYS.VIN)?.value;

  if (!vin) {
    throw new Error('VIN not found in VTP response');
  }

  return {
    vin,
    vtpDetails: extractVtpDetails(items),
    vtpPhotos: extractDealerPhotos(car),
  };
};

/**
 * Converts VTP photos to car image elements for gallery display
 *
 * @param vtpPhotos - Array of VTP photos from the parser
 * @returns Array of car image elements with imageType: 'real'
 */
export const vtpPhotosToCarImageElements = (
  vtpPhotos: IVtpPhoto[]
): Array<{ type: 'image'; title: string; source: string; imageType: 'real' }> =>
  vtpPhotos.map((photo, index) => ({
    type: 'image' as const,
    title: `Dealer Photo ${index + 1}`,
    source: photo.url,
    imageType: 'real' as const,
  }));
